import React, { useState } from 'react';
import {
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

/**
 * 收入報表圖表組件
 * @param {Object} props
 * @param {Array} props.data - 圖表數據
 * @param {string} props.period - 時間週期 (daily, weekly, monthly)
 */
const RevenueReportChart = ({ data = [], period = 'daily' }) => {
    const [showRevenue, setShowRevenue] = useState(true);
    const [showOrders, setShowOrders] = useState(true);
    const [showDrivers, setShowDrivers] = useState(false);
    const [showAverageFare, setShowAverageFare] = useState(false);

    // 格式化日期顯示
    const formatDate = (dateString) => {
        const date = new Date(dateString);

        switch (period) {
            case 'daily':
                return date.toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' });
            case 'weekly':
                return `W${Math.ceil(date.getDate() / 7)}`;
            case 'monthly':
                return date.toLocaleDateString('zh-TW', { month: 'short' });
            default:
                return dateString;
        }
    };

    // 格式化提示框
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
                    <p className="font-medium text-gray-900 mb-2">{label}</p>
                    {payload.map((entry, index) => (
                        <div key={`item-${index}`} className="flex justify-between items-center mb-1">
                            <div className="flex items-center">
                                <div
                                    className="w-3 h-3 mr-2 rounded-full"
                                    style={{ backgroundColor: entry.color }}
                                ></div>
                                <span className="text-sm text-gray-600">{entry.name}:</span>
                            </div>
                            <span className="text-sm font-medium ml-4">
                                {entry.name.includes('收入') ? '$' : ''}
                                {entry.value?.toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        return null;
    };

    // 圖表配置選項
    const chartOptions = [
        { id: 'revenue', label: '收入', color: '#3B82F6', checked: showRevenue, onChange: () => setShowRevenue(!showRevenue) },
        { id: 'orders', label: '訂單數', color: '#10B981', checked: showOrders, onChange: () => setShowOrders(!showOrders) },
        { id: 'drivers', label: '司機數', color: '#F59E0B', checked: showDrivers, onChange: () => setShowDrivers(!showDrivers) },
        { id: 'average_fare', label: '平均車資', color: '#8B5CF6', checked: showAverageFare, onChange: () => setShowAverageFare(!showAverageFare) }
    ];

    // 格式化X軸標籤
    const formatXAxis = (value) => {
        return formatDate(value);
    };

    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                <p className="text-gray-500">無數據</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* 圖表選項 */}
            <div className="flex flex-wrap gap-4">
                {chartOptions.map(option => (
                    <label key={option.id} className="flex items-center">
                        <input
                            type="checkbox"
                            checked={option.checked}
                            onChange={option.onChange}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-2"
                        />
                        <span
                            className="inline-block w-3 h-3 mr-1 rounded-full"
                            style={{ backgroundColor: option.color }}
                        ></span>
                        <span className="text-sm text-gray-700">{option.label}</span>
                    </label>
                ))}
            </div>

            {/* 圖表 */}
            <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatXAxis}
                            scale="auto"
                            padding={{ left: 10, right: 10 }}
                        />
                        <YAxis
                            yAxisId="left"
                            orientation="left"
                            stroke="#3B82F6"
                            label={{ value: '收入 ($)', angle: -90, position: 'insideLeft' }}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            stroke="#10B981"
                            label={{ value: '數量', angle: 90, position: 'insideRight' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />

                        {/* 收入柱狀圖 */}
                        {showRevenue && (
                            <Bar
                                dataKey="total_revenue"
                                name="總收入"
                                yAxisId="left"
                                fill="#3B82F6"
                                barSize={20}
                            />
                        )}

                        {/* 訂單數折線圖 */}
                        {showOrders && (
                            <Line
                                type="monotone"
                                dataKey="total_orders"
                                name="訂單數"
                                yAxisId="right"
                                stroke="#10B981"
                                strokeWidth={2}
                                dot={{ r: 5 }}
                                activeDot={{ r: 8 }}
                            />
                        )}

                        {/* 司機數折線圖 */}
                        {showDrivers && (
                            <Line
                                type="monotone"
                                dataKey="active_drivers"
                                name="活躍司機"
                                yAxisId="right"
                                stroke="#F59E0B"
                                strokeWidth={2}
                                dot={{ r: 5 }}
                                activeDot={{ r: 8 }}
                            />
                        )}

                        {/* 平均車資折線圖 */}
                        {showAverageFare && (
                            <Line
                                type="monotone"
                                dataKey="average_fare"
                                name="平均車資"
                                yAxisId="left"
                                stroke="#8B5CF6"
                                strokeWidth={2}
                                dot={{ r: 5 }}
                                activeDot={{ r: 8 }}
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default RevenueReportChart;